import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon } from '@heroicons/react/24/outline';
import { subscriptionAPI } from '../../services/api';
import './LandingPricingSection.css';

type LandingPlan = {
  id: string;
  name: string;
  price: number;
  credits: number;
  features: string[];
  popular: boolean;
  description?: string;
  sortOrder: number;
};

const LandingPricingSection: React.FC = () => {
  const [plans, setPlans] = useState<LandingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  // Empty currency means "not yet resolved from backend" — the section shows
  // a loading skeleton until the real value lands. Do NOT seed 'usd' here:
  // flashing `$` before `₹` is exactly the bug LT-194 closes (LT-200 sweep).
  const [currency, setCurrency] = useState('');

  useEffect(() => {
    let active = true;

    const loadPlans = async () => {
      try {
        setLoading(true);
        const response = await subscriptionAPI.getSubscriptionLinks();
        const plansPayload = response?.plans || {};
        if (response?.currency) {
          setCurrency(String(response.currency).toLowerCase());
        }

        const mapped = Object.entries(plansPayload)
          .map(([planId, plan]: [string, any]) => ({
            id: planId,
            name: plan?.name || planId,
            price: Number(plan?.price || 0),
            credits: Number(plan?.call_limit || plan?.credits || 0),
            features: Array.isArray(plan?.features) ? plan.features : [],
            popular: Boolean(plan?.popular ?? plan?.is_popular),
            description: plan?.description || '',
            sortOrder: Number(plan?.sort_order ?? 999),
            isActive: plan?.is_active,
          }))
          .filter((plan) => plan.isActive !== false)
          .map(({ isActive, ...plan }) => plan)
          .sort((a, b) => a.sortOrder - b.sortOrder);

        if (active) setPlans(mapped);
      } catch (_) {
        if (active) setPlans([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadPlans();
    return () => {
      active = false;
    };
  }, []);

  const currencySymbol = useMemo(() => {
    if (!currency) return '';
    const symbols: Record<string, string> = {
      usd: '$',
      aud: 'A$',
      eur: '€',
      gbp: '£',
      jpy: '¥',
      cad: 'C$',
      nzd: 'NZ$',
      inr: '₹',
    };
    return symbols[currency] ?? currency.toUpperCase();
  }, [currency]);

  const gridClassName = useMemo(() => {
    if (plans.length >= 4) {
      return 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 max-w-6xl mx-auto';
    }
    if (plans.length === 3) {
      return 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 max-w-5xl mx-auto';
    }
    if (plans.length === 2) {
      return 'grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto';
    }
    return 'grid grid-cols-1 gap-6 max-w-md mx-auto';
  }, [plans.length]);

  return (
    <section
      id="pricing"
      className="landing-pricing landing-section-equal py-8 sm:py-10 lg:py-14 bg-slate-950 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center max-w-3xl mx-auto mb-10 sm:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium bg-emerald-500/15 text-emerald-200 border border-emerald-400/20 mb-3 sm:mb-4">
            Pricing
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-100 mb-4">
            Plans that scale with your outreach
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-slate-300">
            Start free with 20 credits and upgrade when your calling volume grows.
          </p>
        </motion.div>

        {!loading && plans.length === 0 && (
          <div className="text-center text-slate-400 border border-white/10 rounded-xl py-8 max-w-2xl mx-auto">
            Pricing plans are not available right now.
          </div>
        )}
        <div className={gridClassName}>
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.07 }}
              className={`landing-pricing__card ${plan.popular ? 'landing-pricing__card--popular' : ''}`}
            >
              {plan.popular && (
                <span className="landing-pricing__badge">
                  Most popular
                </span>
              )}
              <h3 className="text-xl font-semibold text-slate-100">{plan.name}</h3>
              {plan.description && (
                <p className="text-xs sm:text-sm text-slate-400 mt-2">{plan.description}</p>
              )}
              <div className="mt-3 mb-2">
                <span className="text-3xl font-bold text-slate-100">{currencySymbol}{plan.price}</span>
                <span className="text-slate-400 ml-1">/month</span>
              </div>
              <p className="text-sm text-cyan-300 font-medium">{plan.credits} monthly credits</p>

              <ul className="mt-5 space-y-2.5">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start text-sm text-slate-300">
                    <CheckIcon className="h-4 w-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/signup"
                className={`mt-6 inline-flex w-full justify-center rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                  plan.popular
                    ? 'bg-cyan-600 hover:bg-cyan-700 text-white'
                    : 'bg-slate-700 hover:bg-slate-600 text-white'
                }`}
              >
                Start free
              </Link>
            </motion.div>
          ))}
        </div>
        {loading && (
          <p className="text-center mt-6 text-xs sm:text-sm text-slate-400">
            Loading pricing...
          </p>
        )}
        <p className="text-center mt-6 text-xs sm:text-sm text-slate-400">
          Need extra volume? Add credit packs anytime from your billing dashboard.
        </p>
        <div className="mt-5 text-center">
          <a href="#" className="landing-back-to-top">
            Back to top
            <span aria-hidden="true">↑</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default LandingPricingSection;
